import { useState, type DragEvent } from "react";
import {
  Bot,
  Wrench,
  GitFork,
  Hand,
  CircleDot,
  Square,
  Stethoscope,
  CreditCard,
  Calendar,
  Shield,
  FileText,
  Mail,
  Bell,
  ClipboardList,
  Database,
  ChevronRight,
  PanelLeftClose,
  PanelLeftOpen,
  UserPlus,
  FileCheck,
  ArrowRightLeft,
  ClipboardCheck,
} from "lucide-react";
import { cn } from "../../lib/utils";

export interface PaletteItem {
  type: string;
  label: string;
  icon: React.ReactNode;
  color: string;
}

interface PaletteCategory {
  label: string;
  items: PaletteItem[];
}

const PALETTE_CATEGORIES: PaletteCategory[] = [
  {
    label: "TRIGGERS",
    items: [
      { type: "trigger_clinical", label: "Clinical Event", icon: <Stethoscope className="h-3.5 w-3.5" />, color: "text-emerald-500" },
      { type: "trigger_claims", label: "Claims Event", icon: <CreditCard className="h-3.5 w-3.5" />, color: "text-blue-500" },
      { type: "trigger_scheduling", label: "Scheduling", icon: <Calendar className="h-3.5 w-3.5" />, color: "text-violet-500" },
      { type: "trigger_prior_auth", label: "Prior Auth", icon: <Shield className="h-3.5 w-3.5" />, color: "text-amber-500" },
      { type: "trigger_referral", label: "Referral", icon: <FileText className="h-3.5 w-3.5" />, color: "text-cyan-500" },
    ],
  },
  {
    label: "AGENTS",
    items: [
      { type: "agent", label: "Supervisor", icon: <Bot className="h-3.5 w-3.5" />, color: "text-violet-500" },
      { type: "agent", label: "Worker", icon: <Bot className="h-3.5 w-3.5" />, color: "text-blue-500" },
      { type: "agent", label: "Router", icon: <GitFork className="h-3.5 w-3.5" />, color: "text-amber-500" },
      { type: "agent", label: "Tool Executor", icon: <Wrench className="h-3.5 w-3.5" />, color: "text-emerald-500" },
    ],
  },
  {
    label: "TOOLS",
    items: [
      { type: "tool", label: "EHR Lookup", icon: <Database className="h-3.5 w-3.5" />, color: "text-emerald-500" },
      { type: "tool", label: "Payer Query", icon: <CreditCard className="h-3.5 w-3.5" />, color: "text-blue-500" },
      { type: "tool", label: "Send Email", icon: <Mail className="h-3.5 w-3.5" />, color: "text-orange-500" },
      { type: "tool", label: "Notify Staff", icon: <Bell className="h-3.5 w-3.5" />, color: "text-yellow-500" },
      { type: "tool", label: "Audit Log", icon: <ClipboardList className="h-3.5 w-3.5" />, color: "text-zinc-500" },
      { type: "tool", label: "Push to EHR", icon: <Database className="h-3.5 w-3.5" />, color: "text-teal-500" },
    ],
  },
  {
    label: "FLOW",
    items: [
      { type: "start", label: "Start", icon: <CircleDot className="h-3.5 w-3.5" />, color: "text-emerald-500" },
      { type: "end", label: "End", icon: <Square className="h-3.5 w-3.5" />, color: "text-zinc-500" },
      { type: "interrupt", label: "HITL Review", icon: <Hand className="h-3.5 w-3.5" />, color: "text-orange-500" },
      { type: "router", label: "Router", icon: <GitFork className="h-3.5 w-3.5" />, color: "text-amber-500" },
    ],
  },
  {
    label: "CONTEXTS",
    items: [
      { type: "context_agent:insurance_verification", label: "Insurance Verification", icon: <Shield className="h-3.5 w-3.5" />, color: "text-blue-500" },
      { type: "context_agent:patient_intake", label: "Patient Intake", icon: <UserPlus className="h-3.5 w-3.5" />, color: "text-emerald-500" },
      { type: "context_agent:billing", label: "Billing & Claims", icon: <CreditCard className="h-3.5 w-3.5" />, color: "text-violet-500" },
      { type: "context_agent:prior_auth", label: "Prior Authorization", icon: <FileCheck className="h-3.5 w-3.5" />, color: "text-amber-500" },
      { type: "context_agent:referral", label: "Referral Management", icon: <ArrowRightLeft className="h-3.5 w-3.5" />, color: "text-cyan-500" },
      { type: "context_agent:claims_review", label: "Claims Review", icon: <ClipboardCheck className="h-3.5 w-3.5" />, color: "text-rose-500" },
    ],
  },
];

function getDefaultData(type: string, label: string): Record<string, unknown> {
  if (type === "agent") {
    const agentTypeMap: Record<string, string> = {
      Supervisor: "supervisor",
      Worker: "worker",
      Router: "router",
      "Tool Executor": "tool_executor",
    };
    return {
      name: `New ${label}`,
      agentType: agentTypeMap[label] ?? "general",
      modelId: "us.anthropic.claude-sonnet-4-20250514-v1:0",
    };
  }
  if (type.startsWith("context_agent:")) {
    const contextId = type.replace("context_agent:", "");
    return {
      name: label,
      agentType: "worker",
      modelId: "us.anthropic.claude-sonnet-4-20250514-v1:0",
      contexts: [contextId],
    };
  }
  if (type === "tool") return { name: label, description: "" };
  if (type === "router") return { name: label, conditions: [] };
  if (type === "interrupt") return { name: label, approvalRequired: true };
  if (type.startsWith("trigger_")) return { name: label, eventType: type, filters: {} };
  return {};
}

function onDragStart(event: DragEvent, item: PaletteItem) {
  let nodeType = item.type;
  if (item.type.startsWith("trigger_")) nodeType = "trigger";
  else if (item.type.startsWith("context_agent:")) nodeType = "agent";
  const data = getDefaultData(item.type, item.label);
  event.dataTransfer.setData("application/reactflow-type", nodeType);
  event.dataTransfer.setData("application/reactflow-data", JSON.stringify(data));
  event.dataTransfer.effectAllowed = "move";
}

function CategorySection({ category }: { category: PaletteCategory }) {
  const [open, setOpen] = useState(true);

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex w-full items-center gap-1.5 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors"
      >
        <ChevronRight className={cn("h-3 w-3 transition-transform", open && "rotate-90")} />
        {category.label}
        <span className="ml-auto text-[9px] font-normal tabular-nums">{category.items.length}</span>
      </button>
      {open && (
        <div className="space-y-0.5 ml-1">
          {category.items.map((item, i) => (
            <div
              key={`${item.type}-${i}`}
              draggable
              onDragStart={(e) => onDragStart(e, item)}
              className="flex items-center gap-2 rounded-md px-2.5 py-1.5 text-xs cursor-grab active:cursor-grabbing hover:bg-accent/50 transition-colors"
            >
              <span className={item.color}>{item.icon}</span>
              <span className="text-foreground/80">{item.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

interface PaletteProps {
  collapsed: boolean;
  onToggle: () => void;
}

export function Palette({ collapsed, onToggle }: PaletteProps) {
  if (collapsed) {
    return (
      <div className="w-10 shrink-0 border-r border-border bg-card/50 flex flex-col items-center pt-2">
        <button
          type="button"
          onClick={onToggle}
          className="p-1.5 rounded-md hover:bg-accent text-muted-foreground hover:text-foreground transition-colors"
          aria-label="Expand palette"
        >
          <PanelLeftOpen className="h-4 w-4" />
        </button>
      </div>
    );
  }

  return (
    <div className="w-56 shrink-0 border-r border-border bg-card/50 flex flex-col">
      <div className="flex items-center justify-between px-3 py-2 border-b border-border/50">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Palette</span>
        <button
          type="button"
          onClick={onToggle}
          className="p-1 rounded-md hover:bg-accent text-muted-foreground hover:text-foreground transition-colors"
          aria-label="Collapse palette"
        >
          <PanelLeftClose className="h-3.5 w-3.5" />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto scrollbar-auto-hide px-2.5 py-2 space-y-3">
        {PALETTE_CATEGORIES.map((cat) => (
          <CategorySection key={cat.label} category={cat} />
        ))}
      </div>
    </div>
  );
}

export { PALETTE_CATEGORIES, getDefaultData };
